import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { History, Landmark, Megaphone, Plus, Scale, SendHorizonal, ThumbsDown, ThumbsUp, Trash2, type LucideIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { ApiError, del, get, post, postStream } from '../lib/api.ts'
import { fmtDateTime, pagesLabel } from '../lib/format.ts'
import { useCondo } from '../lib/hooks.ts'
import type { Citation } from '../lib/types.ts'
import { Markdown } from '../components/Markdown.tsx'
import { Button, cn, Dialog, ErrorNote, Field, Spinner, Textarea } from '../components/ui.tsx'
import { t } from '../lib/i18n.ts'

/** Quick actions (uCondo's "prompts prontos"): one click, one call over data the caller may see, saved as a conversation. */
const QUICK_ACTIONS: { kind: string; cap: string; label: string; hint: string; icon: LucideIcon }[] = [
  { kind: 'analise_convencao', cap: 'documents.manage', label: t('Analisar convenção e regimento'), hint: t('Pontos de atenção, contradições e cláusulas desatualizadas, com artigo e página.'), icon: Scale },
  { kind: 'analise_balancete', cap: 'finance.view', label: t('Analisar os balancetes'), hint: t('Variações mês a mês, itens fora do padrão e perguntas para a administradora.'), icon: Landmark },
  { kind: 'comunicado', cap: 'notices.publish', label: t('Rascunhar um comunicado'), hint: t('Descreva o assunto; o texto sai pronto para revisar e publicar em Avisos.'), icon: Megaphone },
]
const DRAFT_KEY = 'subsindico:draft-announcement'

interface Msg {
  id: string
  role: 'user' | 'assistant'
  content: string
  citations: Citation[]
  error?: string | null
  feedback?: number | null
  pending?: boolean
}

const SUGGESTIONS = [
  t('Até que horas pode ter barulho?'),
  t('Como faço para reservar o salão de festas?'),
  t('Qual é a multa por atraso na taxa de condomínio?'),
  t('Posso fazer obra no apartamento no sábado?'),
  t('O que foi decidido na última assembleia?'),
]

const CATEGORY: Record<string, string> = { manual: t('Manual do prédio'), convencao: t('Convenção'), regimento: t('Regimento interno'), ata: t('Ata'), edital: t('Edital'), contrato: t('Contrato'), laudo: t('Laudo'), avcb: 'AVCB', apolice: t('Apólice'), balancete: t('Balancete'), orcamento: t('Orçamento'), outro: t('Documento') }

export function Assistant() {
  const { slug, cid } = useParams({ strict: false }) as { slug: string; cid?: string }
  const { condoId, can } = useCondo(slug)
  const nav = useNavigate()
  const qc = useQueryClient()
  const [messages, setMessages] = useState<Msg[]>([])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [showList, setShowList] = useState(false)
  const [focusCite, setFocusCite] = useState<string | null>(null)
  const bottom = useRef<HTMLDivElement>(null)
  const pendingQ = useRef<string | null>(new URLSearchParams(location.search).get('q'))
  const status = useQuery({ queryKey: ['ai', condoId], queryFn: () => get<{ available: boolean }>(`/api/condos/${condoId}/ai/status`), enabled: Boolean(condoId) })
  const list = useQuery({ queryKey: ['convs', condoId], queryFn: () => get<{ conversations: { id: string; title: string; channel: string; updated_at: string }[] }>(`/api/condos/${condoId}/conversations`), enabled: Boolean(condoId) })
  const conv = useQuery({
    queryKey: ['conv', condoId, cid],
    queryFn: () => get<{ conversation: { id: string; title: string }; messages: Msg[] }>(`/api/condos/${condoId}/conversations/${cid}`),
    enabled: Boolean(condoId && cid),
  })
  const [acting, setActing] = useState<string | null>(null)
  const [draftOpen, setDraftOpen] = useState(false)
  const [draftInput, setDraftInput] = useState('')
  const actions = QUICK_ACTIONS.filter((a) => can(a.cap))
  const runAction = async (kind: string, input?: string) => {
    if (acting) return
    setActing(kind)
    try {
      const r = await post<{ conversationId: string }>(`/api/condos/${condoId}/ai/actions/${kind}`, input ? { input } : {})
      qc.invalidateQueries({ queryKey: ['convs', condoId] })
      setDraftOpen(false)
      setDraftInput('')
      nav({ to: '/c/$slug/assistente/$cid', params: { slug, cid: r.conversationId } })
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setActing(null)
    }
  }
  const isDraft = Boolean(cid && conv.data?.conversation?.title.startsWith('Rascunho de comunicado'))
  const lastAnswer = [...messages].reverse().find((m) => m.role === 'assistant' && !m.pending && !m.error)
  const publishDraft = () => {
    if (!lastAnswer) return
    const lines = lastAnswer.content.trim().split('\n')
    const title = (lines[0] ?? '').replace(/^#+\s*/, '').trim().slice(0, 160) || 'Comunicado'
    const bodyMd = lines.slice(1).join('\n').trim()
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ title, bodyMd }))
    } catch {
      /* private window: the user can still copy the text */
    }
    nav({ to: '/c/$slug/avisos', params: { slug } })
  }
  useEffect(() => {
    if (conv.data && !busy) setMessages(conv.data.messages)
    if (!cid) setMessages([])
  }, [conv.data, cid])
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, messages.at(-1)?.content])

  const send = async (question: string) => {
    const q = question.trim()
    if (!q || busy) return
    setError(null)
    setBusy(true)
    setText('')
    try {
      let id = cid
      if (!id) {
        id = (await post<{ id: string }>(`/api/condos/${condoId}/conversations`)).id
        nav({ to: '/c/$slug/assistente/$cid', params: { slug, cid: id }, replace: true })
      }
      const tmp: Msg = { id: `a-${Date.now()}`, role: 'assistant', content: '', citations: [], pending: true }
      setMessages((m) => [...m, { id: `u-${Date.now()}`, role: 'user', content: q, citations: [] }, tmp])
      await postStream(`/api/condos/${condoId}/conversations/${id}/messages`, { text: q }, (ev, data) => {
        setMessages((m) => {
          const copy = [...m]
          const last = { ...copy[copy.length - 1]! }
          if (ev === 'delta') last.content += data.text
          if (ev === 'done') { last.citations = data.citations; last.pending = false; last.id = data.messageId; if (data.content) last.content = data.content }
          if (ev === 'error') { last.error = data.message; last.pending = false }
          copy[copy.length - 1] = last
          return copy
        })
      })
      qc.invalidateQueries({ queryKey: ['convs', condoId] })
    } catch (e) {
      setError(e)
      setMessages((m) => (m.at(-1)?.pending ? m.slice(0, -2) : m))
      setText(q)
    } finally {
      setBusy(false)
    }
  }
  useEffect(() => {
    if (pendingQ.current && condoId && status.data?.available) {
      const q = pendingQ.current
      pendingQ.current = null
      history.replaceState(null, '', location.pathname)
      void send(q)
    }
  }, [condoId, status.data?.available])

  const removeConv = useMutation({
    mutationFn: (id: string) => del(`/api/condos/${condoId}/conversations/${id}`),
    onSuccess: (_r, id) => { qc.invalidateQueries({ queryKey: ['convs', condoId] }); if (id === cid) nav({ to: '/c/$slug/assistente', params: { slug } }) },
  })
  const feedback = useMutation({
    mutationFn: (v: { mid: string; value: 1 | -1 | 0 }) => post(`/api/condos/${condoId}/conversations/${cid}/messages/${v.mid}/feedback`, { value: v.value }),
    onSuccess: (_r, v) => setMessages((ms) => ms.map((x) => (x.id === v.mid ? { ...x, feedback: v.value === 0 ? null : v.value } : x))),
  })

  const ConvList = (
    <div className="flex flex-col gap-1">
      <Link to="/c/$slug/assistente" params={{ slug }} onClick={() => setShowList(false)} className="mb-2 inline-flex min-h-10 items-center gap-2 rounded-ficha border border-pauta bg-folha px-3 font-semibold text-caneta hover:border-caneta">
        <Plus className="size-4" />{' '}{t('Nova conversa')}
      </Link>
      {list.data?.conversations.map((c) => (
        <div key={c.id} className={cn('group flex items-center rounded-ficha', c.id === cid ? 'bg-caneta-clara' : 'hover:bg-folha')}>
          <Link to="/c/$slug/assistente/$cid" params={{ slug, cid: c.id }} onClick={() => setShowList(false)} className="min-w-0 flex-1 px-3 py-2">
            <span className="block truncate text-[0.95rem] font-semibold">{c.title}</span>
            <span className="text-xs text-tinta-2">{fmtDateTime(c.updated_at)}{c.channel === 'mcp' ? t(', via MCP') : c.channel === 'whatsapp' ? t(', via WhatsApp') : ''}</span>
          </Link>
          <button className="p-2 text-tinta-3 opacity-0 group-hover:opacity-100 hover:text-margem focus:opacity-100" aria-label={t('Apagar conversa')} onClick={() => removeConv.mutate(c.id)}>
            <Trash2 className="size-4" />
          </button>
        </div>
      ))}
    </div>
  )

  if (!can('assistant.use')) return <ErrorNote error={new Error(t('Seu perfil não usa o assistente.'))} />
  return (
    <div className="-mx-4 -my-5 sm:-mx-6 sm:-my-8 lg:grid lg:grid-cols-[15rem_1fr]">
      <aside className="hidden border-r border-pauta bg-papel p-3 lg:block">{ConvList}</aside>
      <div className="almaco relative flex min-h-[calc(100dvh-8.5rem)] flex-col md:min-h-dvh">
        <div className="flex items-center justify-between py-3 pr-4 pl-[3.5rem]">
          <h1 className="font-doc text-[1.35rem] font-semibold">{t('Pergunte ao Subsíndico')}</h1>
          <button onClick={() => setShowList((v) => !v)} className="inline-flex items-center gap-1 text-[0.95rem] font-semibold text-caneta lg:hidden"><History className="size-4" />{' '}{t('Conversas')}</button>
        </div>
        {showList && <div className="border-y border-pauta bg-papel p-3 lg:hidden">{ConvList}</div>}
        <Dialog open={draftOpen} onClose={() => setDraftOpen(false)} title={t('Rascunhar um comunicado')}>
          <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void runAction('comunicado', draftInput.trim()) }}>
            <Field label={t('Sobre o que é o comunicado?')} htmlFor="qa-in" hint={t('O que, quando, onde e o que o morador precisa fazer. O texto sai como rascunho para você revisar.')}>
              <Textarea id="qa-in" rows={4} value={draftInput} onChange={(e) => setDraftInput(e.target.value)} minLength={5} maxLength={1500} required placeholder={t('Ex.: sem água na quinta das 9h às 12h, troca do registro da coluna B; pedir para guardar água')} />
            </Field>
            <div className="flex justify-end gap-2"><Button type="button" variant="quiet" onClick={() => setDraftOpen(false)}>{t('Cancelar')}</Button><Button type="submit" busy={acting === 'comunicado'} disabled={draftInput.trim().length < 5}>{t('Rascunhar')}</Button></div>
          </form>
        </Dialog>

        <div className="flex-1 pr-4 pb-4 pl-[3.5rem] sm:pr-8">
          {cid && conv.isLoading ? <Spinner /> : messages.length === 0 ? (
            <div className="max-w-xl pt-4">
              <p className="font-doc text-[1.15rem] leading-[1.75rem] text-tinta-2">
                {t('Respondo com base na convenção, no regimento interno, nas atas e nos outros documentos do acervo que você pode ver. Cada resposta traz a fonte, marcada assim:')}{' '}<span className="marca font-semibold text-tinta">[1]</span>.
              </p>
              <div className="mt-6 flex flex-col items-start gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} disabled={!status.data?.available} className="rounded-ficha border border-pauta bg-folha/80 px-3 py-2 text-left hover:border-caneta hover:text-caneta disabled:opacity-60">
                    {s}
                  </button>
                ))}
              </div>
              {actions.length > 0 && (
                <div className="mt-8">
                  <p className="mb-2 font-semibold">{t('Ações rápidas')}</p>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {actions.map((a) => (
                      <button key={a.kind} type="button" onClick={() => (a.kind === 'comunicado' ? setDraftOpen(true) : void runAction(a.kind))} disabled={!status.data?.available || acting !== null} className="flex flex-col items-start gap-1 rounded-ficha border border-pauta bg-folha/80 p-3 text-left hover:border-caneta disabled:opacity-60">
                        <span className="inline-flex items-center gap-2 font-semibold text-caneta"><a.icon className="size-4" /> {a.label}</span>
                        <span className="text-sm text-tinta-2">{acting === a.kind ? t('Analisando… pode levar um minuto.') : a.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <ol className="flex max-w-3xl flex-col gap-7 pt-2">
              {messages.map((m) => (
                <li key={m.id}>
                  {m.role === 'user' ? (
                    <p className="font-doc text-[1.15rem] leading-[1.75rem] font-semibold text-caneta">{m.content}</p>
                  ) : (
                    <Answer m={m} focus={focusCite} onCite={(n) => setFocusCite(`${m.id}:${n}`)} slug={slug} onFeedback={cid && !m.pending && !m.error && !m.id.startsWith('a-') ? (value) => feedback.mutate({ mid: m.id, value }) : undefined} />
                  )}
                </li>
              ))}
            </ol>
          )}
          {isDraft && lastAnswer && can('notices.publish') && (
            <div className="mt-6 max-w-3xl">
              <Button variant="outline" onClick={publishDraft}><Megaphone className="size-5" />{' '}{t('Revisar e publicar em Avisos')}</Button>
            </div>
          )}
          <div ref={bottom} />
        </div>

        <form className="sticky bottom-16 border-t border-pauta bg-folha/95 py-3 pr-4 pl-[3.5rem] backdrop-blur md:bottom-0 sm:pr-8" onSubmit={(e) => { e.preventDefault(); void send(text) }}>
          {!status.isLoading && !status.data?.available && (
            <p className="mb-2 max-w-3xl text-[0.95rem] text-alerta">
              {t('A IA ainda não está configurada para este condomínio. Você pode')}{' '}<Link to="/perfil" hash="mcp" className="font-semibold underline">{t('conectar o seu próprio assistente (Claude, ChatGPT…) via MCP')}</Link>{' '}{t('ou o síndico pode configurar uma chave em Configurações.')}
            </p>
          )}
          {error instanceof ApiError || error instanceof Error ? <div className="mb-2"><ErrorNote error={error} /></div> : null}
          <div className="flex max-w-3xl items-end gap-2">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(text) } }}
              rows={1}
              maxLength={2000}
              placeholder={t('Escreva sua pergunta')}
              aria-label={t('Sua pergunta')}
              className="min-h-11 resize-none"
            />
            <Button type="submit" busy={busy} disabled={!text.trim() || !status.data?.available} aria-label={t('Enviar pergunta')}><SendHorizonal className="size-5" /></Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Answer({ m, onCite, focus, slug, onFeedback }: { m: Msg; onCite: (n: number) => void; focus: string | null; slug: string; onFeedback?: (value: 1 | -1 | 0) => void }) {
  const fb = m.feedback ?? null
  return (
    <div className="text-[1.05rem] leading-[1.75rem]">
      {m.content ? <Markdown text={m.content} onCite={onCite} /> : m.pending ? <p className="text-tinta-2">{t('Procurando nos documentos…')}</p> : null}
      {m.error && <p className="mt-1 text-[0.95rem] font-semibold text-margem">{m.error}</p>}
      {onFeedback && (
        <p className="mt-1 flex items-center gap-1 text-sm text-tinta-2">
          <span className="mr-1">{t('A resposta ajudou?')}</span>
          <button type="button" aria-label={t('Resposta útil')} aria-pressed={fb === 1} onClick={() => onFeedback(fb === 1 ? 0 : 1)} className={cn('rounded p-1.5 hover:bg-papel', fb === 1 ? 'text-carimbo' : 'text-tinta-3')}><ThumbsUp className="size-4" /></button>
          <button type="button" aria-label={t('Resposta não ajudou')} aria-pressed={fb === -1} onClick={() => onFeedback(fb === -1 ? 0 : -1)} className={cn('rounded p-1.5 hover:bg-papel', fb === -1 ? 'text-margem' : 'text-tinta-3')}><ThumbsDown className="size-4" /></button>
        </p>
      )}
      {m.citations.length > 0 && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {m.citations.map((c) => {
            const id = `${m.id}:${c.n}`
            return (
              <Link
                key={c.n}
                id={id}
                to={c.category === 'manual' ? '/c/$slug/predio' : '/c/$slug/acervo/$docId'}
                params={{ slug, docId: c.documentId } as never}
                search={(c.pageFrom ? { pagina: c.pageFrom } : {}) as never}
                className={cn('block rounded-ficha border border-pauta bg-folha px-3 py-2.5 shadow-[0_1px_0_var(--color-pauta)] hover:border-caneta', focus === id && 'ring-2 ring-marca')}
              >
                <span className="flex items-baseline gap-2 text-sm">
                  <span className="grid size-5 shrink-0 place-items-center rounded-sm bg-marca text-[0.72rem] font-bold">{c.n}</span>
                  <span className="truncate font-semibold">{c.title}</span>
                  <span className="ml-auto shrink-0 text-tinta-2">{pagesLabel(c.pageFrom, c.pageTo)}</span>
                </span>
                <span className="mt-1 block text-xs text-tinta-2">{CATEGORY[c.category] ?? t('Documento')}{c.headingPath ? `, ${c.headingPath}` : ''}</span>
                <span className="mt-1.5 line-clamp-3 block font-doc text-[0.92rem] leading-snug text-tinta">
                  <span className="marca">{c.snippet}</span>
                </span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
